import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning,
  NetworkCheck,
  Security,
  Info,
  Refresh,
  Home,
  ExpandMore,
  ExpandLess,
  Clear
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from '../../stores/errorStore';
import { AppError } from '../../types/errors';
import { log } from '../../utils/errors/logger';
import { formatTime } from '../../utils/formatting/formatters';
import { isDev } from '../../config/env';

interface GlobalErrorDialogProps {
  maxErrors?: number;
  showRetryActions?: boolean;
  showDismissAll?: boolean;
  onClose?: () => void;
}

const ErrorIcon_MAPPING = {
  network: NetworkCheck,
  auth: Security,
  server: ErrorIcon,
  validation: Warning,
  unknown: Info
};

const ERROR_SEVERITY: Record<AppError['type'], 'error' | 'warning' | 'info'> = {
  network: 'error',
  auth: 'error', 
  server: 'error',
  validation: 'warning',
  unknown: 'info'
};

export const GlobalErrorDialog: React.FC<GlobalErrorDialogProps> = ({
  maxErrors = 5,
  showRetryActions = true,
  showDismissAll = true,
  onClose
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const [expandedError, setExpandedError] = React.useState<string | null>(null);

  const isOpen = errorHandler.showErrorDialogState && errorHandler.hasErrors;
  const displayErrors = errorHandler.errors.slice(0, maxErrors);
  
  // Sort errors by timestamp (newest first) and severity
  const sortedErrors = [...displayErrors].sort((a, b) => {
    // Critical errors first
    if (a.type === 'auth' && b.type !== 'auth') {return -1;}
    if (b.type === 'auth' && a.type !== 'auth') {return 1;}
    if (a.type === 'network' && b.type !== 'network') {return -1;}
    if (b.type === 'network' && a.type !== 'network') {return 1;}
    
    // Then by timestamp
    return b.timestamp - a.timestamp;
  });

  const handleClose = () => {
    errorHandler.dismissErrorDialog();
    
    if (onClose) {
      onClose();
    }
    
    log.user.action('dismiss_error_dialog', 'GlobalErrorDialog');
  };

  const handleRetryError = async (error: AppError) => {
    if (!error.retryable || !errorHandler.canRetry(error.id)) {
      return;
    }
    
    log.user.action('retry_error', 'GlobalErrorDialog', { errorId: error.id });
    
    // For now, just trigger a health check for network errors
    if (error.type === 'network') {
      await errorHandler.checkHealth();
    }
    
    errorHandler.removeError(error.id);
  };

  const handleDismissError = (errorId: string) => {
    errorHandler.removeError(errorId);
    log.user.action('dismiss_single_error', 'GlobalErrorDialog', { errorId });
  };

  const handleDismissAll = () => {
    errorHandler.clearErrors();
    handleClose();
    log.user.action('dismiss_all_errors', 'GlobalErrorDialog');
  };

  const handleGoHome = () => {
    log.user.navigation('GlobalErrorDialog', 'error_dialog_home');
    handleClose();
    navigate('/');
  };

  const toggleErrorExpansion = (errorId: string) => {
    setExpandedError(expandedError === errorId ? null : errorId);
  };

  if (!isOpen) {return null;}

  const hasAuthErrors = sortedErrors.some(error => error.type === 'auth');
  const hasNetworkErrors = sortedErrors.some(error => error.type === 'network');

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="global-error-dialog-title"
    >
      <DialogTitle id="global-error-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorIcon color="error" />
        {t('error.dialog.title', 'Application Errors')}
        <Box sx={{ flexGrow: 1 }} />
        {sortedErrors.length > 1 && (
          <Chip 
            label={`${sortedErrors.length} ${t('error.dialog.errors', 'errors')}`}
            size="small"
            color="error"
            variant="outlined"
          />
        )}
      </DialogTitle>

      <DialogContent>
        {hasAuthErrors && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            <Typography variant="body2">
              {t('error.dialog.authWarning', 'Authentication errors detected. You may need to log in again.')}
            </Typography>
          </Alert>
        )}

        {hasNetworkErrors && (
          <Alert severity="warning" sx={{ marginBottom: 2 }}>
            <Typography variant="body2">
              {t('error.dialog.networkWarning', 'Network errors detected. Please check your connection.')}
            </Typography>
          </Alert>
        )}

        <DialogContentText>
          {sortedErrors.length === 1
            ? t('error.dialog.singleMessage', 'An error occurred that requires your attention:')
            : t('error.dialog.multipleMessage', 'Multiple errors occurred that require your attention:')
          }
        </DialogContentText>

        <List dense>
          {sortedErrors.map((error) => {
            const IconComponent = ErrorIcon_MAPPING[error.type];
            const isExpanded = expandedError === error.id;
            
            return (
              <React.Fragment key={error.id}>
                <ListItem
                  sx={{
                    borderRadius: 1,
                    marginBottom: 1,
                    backgroundColor: ERROR_SEVERITY[error.type] === 'error' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(237, 108, 2, 0.1)',
                    '&:hover': {
                      backgroundColor: ERROR_SEVERITY[error.type] === 'error' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(237, 108, 2, 0.2)',
                    },
                    color: 'text.primary',
                    border: 1,
                    borderColor: ERROR_SEVERITY[error.type] === 'error' ? 'error.main' : 'warning.main'
                  }}
                >
                  <ListItemIcon>
                    <IconComponent color={ERROR_SEVERITY[error.type]} fontSize="small" />
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {error.message}
                        </Typography>
                        <Chip 
                          label={error.type} 
                          size="small" 
                          variant="outlined"
                          color={ERROR_SEVERITY[error.type]}
                        />
                      </Box>
                    }
                    secondary={error.context && (
                      <Typography variant="caption" color="textSecondary">
                        {error.context} • {formatTime(error.timestamp, i18n.language)}
                      </Typography>
                    )}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {error.retryable && errorHandler.canRetry(error.id) && showRetryActions && (
                      <IconButton
                        size="small"
                        onClick={() => handleRetryError(error)}
                        title={t('error.dialog.retry', 'Retry')}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    )}
                    
                    <IconButton
                      size="small"
                      onClick={() => handleDismissError(error.id)}
                      title={t('error.dialog.dismiss', 'Dismiss')}
                    >
                      <Clear fontSize="small" />
                    </IconButton>

                    {isDev && Boolean(error.details) && (
                      <IconButton
                        size="small"
                        onClick={() => toggleErrorExpansion(error.id)}
                        title={t('error.dialog.details', 'Details')}
                      >
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>
                </ListItem>

                {isDev && isExpanded && Boolean(error.details) && (
                  <Collapse in={isExpanded}>
                    <Box sx={{ 
                      marginLeft: 4, 
                      marginBottom: 1, 
                      padding: 1, 
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider'
                    }}>
                      <Typography variant="caption" component="pre" sx={{ 
                        fontSize: '0.7rem', 
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace'
                      }}>
                        {JSON.stringify(error.details, null, 2)}
                      </Typography>
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>

        {errorHandler.errors.length > maxErrors && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginTop: 1 }}>
            {t('error.dialog.moreErrors', 'And {{count}} more errors...', { 
              count: errorHandler.errors.length - maxErrors 
            })}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {showDismissAll && sortedErrors.length > 1 && (
          <Button onClick={handleDismissAll} color="error" variant="text">
            {t('error.dialog.dismissAll', 'Dismiss All')}
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {(hasAuthErrors || hasNetworkErrors) && (
          <Button onClick={handleGoHome} startIcon={<Home />} variant="outlined">
            {t('error.dialog.home', 'Go Home')}
          </Button>
        )}

        <Button onClick={handleClose} variant="contained">
          {t('error.dialog.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Auto-mounting version that manages its own state
export const AutoGlobalErrorDialog: React.FC<Omit<GlobalErrorDialogProps, 'onClose'>> = (props) => {
  const errorHandler = useErrorHandler();

  // Auto-show dialog for critical errors
  React.useEffect(() => {
    const hasAuthErrors = errorHandler.errors.some(e => e.type === 'auth');
    const hasCriticalErrors = errorHandler.errors.some(e => 
      e.type === 'auth' || (e.type === 'server' && !e.retryable)
    );

    if ((hasAuthErrors || hasCriticalErrors) && !errorHandler.showErrorDialogState) {
      setTimeout(() => {
        errorHandler.dismissErrorDialog();
        errorHandler.showErrorDialog(true);
      }, 500); // Small delay to avoid flash
    }
  }, [errorHandler.errors, errorHandler.showErrorDialogState]);

  return <GlobalErrorDialog {...props} />;
};
